/**
 * SearchResultItem Component
 * Individual track item in search results
 */

import { useState } from 'react';
import { Plus, Play, GripVertical } from 'lucide-react';
import { useDraggable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import type { Track, AudioFeatures } from '../../api/types';
import { useWorkbenchStore } from '../../stores/workbench';
import { useTrackGenres } from '../../api/hooks/useGenres';
import { Badge } from '../shared/Badge';

interface SearchResultItemProps {
  track: Track;
  features?: AudioFeatures | null;
  matchScore?: number; // For smart slot recommendations
}

/**
 * Format duration from milliseconds to MM:SS
 */
function formatDuration(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

/**
 * Convert Spotify key number to musical key notation
 */
function formatKey(keyNum: number, mode: 0 | 1): string {
  const keys = ['C', 'C♯', 'D', 'D♯', 'E', 'F', 'F♯', 'G', 'G♯', 'A', 'A♯', 'B'];
  const modeStr = mode === 1 ? 'maj' : 'min';
  return `${keys[keyNum]}${modeStr}`;
}

export function SearchResultItem({ track, features, matchScore }: SearchResultItemProps) {
  const [isHovered, setIsHovered] = useState(false);
  const addTrack = useWorkbenchStore((state) => state.addTrack);

  // Fetch genre data for this track
  const { data: genreData } = useTrackGenres(track.id);
  const topGenres = genreData?.genres.slice(0, 3) || [];

  // Make track draggable
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    isDragging,
  } = useDraggable({
    id: `discovery-${track.id}`,
    data: {
      type: 'external-track',
      track,
    },
  });

  const style = {
    transform: CSS.Translate.toString(transform),
    opacity: isDragging ? 0.5 : 1,
  };

  const handleAddToWorkbench = () => {
    addTrack(track);
  };

  const artistNames = track.artists.map((a) => a.name).join(', ');
  const albumArt = track.album.images?.[2]?.url || track.album.images?.[0]?.url;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`group flex items-center gap-2 p-2 rounded-md hover:bg-zinc-800/50 transition-colors ${
        isDragging ? 'cursor-grabbing' : 'cursor-pointer'
      }`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Drag Handle */}
      <div
        {...attributes}
        {...listeners}
        className="cursor-grab active:cursor-grabbing opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
        title="Drag to workbench"
      >
        <GripVertical className="w-4 h-4 text-zinc-500" />
      </div>

      {/* Album Art */}
      <div className="relative w-12 h-12 flex-shrink-0">
        {albumArt ? (
          <img
            src={albumArt}
            alt={track.album.name}
            className="w-full h-full rounded object-cover"
          />
        ) : (
          <div className="w-full h-full rounded bg-zinc-800 flex items-center justify-center">
            <Play className="w-5 h-5 text-zinc-600" />
          </div>
        )}

        {/* Play Preview Button (on hover) */}
        {isHovered && (
          <button
            className="absolute inset-0 bg-black/60 rounded flex items-center justify-center"
            aria-label="Play preview"
          >
            <Play className="w-5 h-5 text-white fill-white" />
          </button>
        )}
      </div>

      {/* Track Info */}
      <div className="flex-1 min-w-0">
        <div className="font-medium text-sm text-zinc-100 truncate">{track.name}</div>
        <div className="text-xs text-zinc-400 truncate">{artistNames}</div>

        {/* Audio Features */}
        {features && (
          <div className="flex items-center gap-2 mt-1 text-xs text-zinc-500">
            <span>{Math.round(features.tempo)} BPM</span>
            <span>•</span>
            <span>{formatKey(features.key, features.mode)}</span>
            <span>•</span>
            <span>{formatDuration(track.durationMs)}</span>
          </div>
        )}

        {/* Genre Tags */}
        {topGenres.length > 0 && (
          <div className="flex items-center gap-1 mt-1 flex-wrap">
            {topGenres.map((genre) => (
              <Badge key={genre.name} variant="default" size="sm">
                {genre.name}
              </Badge>
            ))}
          </div>
        )}
      </div>

      {/* Match Score Badge (if from smart slot recs) */}
      {matchScore !== undefined && (
        <div className="flex-shrink-0">
          <div className="px-2 py-1 rounded-full bg-emerald-500/10 text-emerald-500 text-xs font-medium">
            {Math.round(matchScore * 100)}%
          </div>
        </div>
      )}

      {/* Add Button */}
      <button
        onClick={handleAddToWorkbench}
        className="flex-shrink-0 p-2 rounded-md bg-emerald-500/10 text-emerald-500 hover:bg-emerald-500 hover:text-white transition-colors opacity-0 group-hover:opacity-100"
        aria-label="Add to workbench"
      >
        <Plus className="w-4 h-4" />
      </button>
    </div>
  );
}
